import React from 'react'
import { useSelector } from 'react-redux'
import { Translate } from 'react-redux-i18n'
import { Cell } from 'recharts'
import { ChartContainer } from '../../../../../../../common/chart/chartContainer'
import Bar from '../../../../../../../common/chart/chartContainer/Bar'
import { Legend } from '../../../../../../../common/chart/legend'
import { UseI18n } from '../../../../../../../common/hooks/useI18n'
import { Icon } from '../../../../../../../common/html/icon'
import { Image } from '../../../../../../../common/image'
import { Loading } from '../../../../../../../common/loading'
import { Notification } from '../../../../../../../common/notification'
import { Tooltip } from '../../../../../../../common/tooltip'
import { formatVal, valToPercent } from '../../../../../../../helpers/value'
import {
  selectLoading,
  selectRelativeContributions,
} from '../../../../store/slice'
import arrowDown from './arrowDown.svg'
import arrowUp from './arrowUp.svg'
import s from './index.module.scss'

const RelativeContributions = ({ isDynamicReport }) => {
  const color = [
    '#B3CC85',
    '#DDBBCD',
    '#E6964C',
    '#E3A2A7',
    '#F5CBA3',
    '#BEDACD',
    '#AE93E1',
    '#8EA492',
    '#1B998B',
    '#717ECD',
    '#D96383',
    '#C3D785',
    '#4EB1CB',
    '#D4A5A5',
    '#9BC4BC',
    '#E6C229',
    '#C97B84',
    '#7A9CC6',
    '#B8A7D0',
    '#F0B67F',
    '#88B04B',
  ]
  const loading = useSelector(selectLoading('riskDriver'))
  const relativeContributions = useSelector(selectRelativeContributions)
  const locale = useSelector((state) => state.i18n.locale)

  const data = relativeContributions.map((item, idx) => ({
    ...item,
    color: color[idx % color.length],
  }))

  return (
    <div className={s.box}>
      <div className={s.title}>
        <Translate value="riskComponentAnalysis.relativeContributions" />
        {!isDynamicReport && (
          <Tooltip
            contentStyle={{
              display: 'flex',
              alignItems: 'center',
              paddingLeft: 4,
            }}
            tooltipStyle={{
              maxWidth: 400,
            }}
            tooltipContent={
              <div className="textCenter" style={{ textAlign: 'justify' }}>
                <Translate value="riskComponentAnalysis.relativeContributionsTooltip" />
              </div>
            }
          >
            <Icon
              className="icon-info-circle"
              style={{ fontSize: 12, color: '#9C9C9C' }}
            />
          </Tooltip>
        )}
      </div>

      {loading ? (
        <div className="positionRelative" style={{ height: 200 }}>
          <Loading />
        </div>
      ) : !data.length ? (
        <div className="dFlex justifyContentCenter" style={{ height: 300 }}>
          <Notification />
        </div>
      ) : (
        <div id="relativeContributionsChartId">
          <div className={s.chartsWrapper}>
            <YAxisLabel medianScore={data?.[0]?.segmentMedianScoreValue} />
            <div className="h100 flex1 mb1">
              <ChartContainer
                data={data}
                width="100%"
                height={250}
                xAxis={[
                  {
                    dataKey: 'riskDriverName',
                    type: 'category',
                    hide: true,
                  },
                ]}
                yAxis={[
                  {
                    yAxisId: 'left',
                    dataKeys: ['relative'],
                    isBarChart: true,
                    isOppositeTick: true,
                    tickFormatter: (val) => formatVal(val, locale),
                    unit: '%',
                  },
                ]}
                tooltip={{
                  schema: {
                    content: [
                      {
                        dataKey: 'relative',
                        label: 'riskComponentAnalysis.relative',
                        isI18n: true,
                        formatValue: (value) =>
                          valToPercent(value, locale, true, true, 4),
                      },
                    ],
                  },
                }}
              >
                <Bar yAxisId={'left'} dataKey={'relative'} maxBarSize={30}>
                  {data.map((item, index) => {
                    return <Cell key={`cell-${index}`} fill={item.color} />
                  })}
                </Bar>
              </ChartContainer>
            </div>
          </div>
          <Legend
            items={data.map((item, index) => ({
              ...item,
              title: item.riskDriverName,
              color: item.color,
            }))}
            className={s.legendGrid3}
            style={{
              gridTemplateRows: `repeat(${Math.ceil(data.length / 3)}, 1fr)`,
            }}
          />
        </div>
      )}
    </div>
  )
}

export default RelativeContributions

const YAxisLabel = ({ medianScore }) => {
  const labelImprovement = UseI18n('riskComponentAnalysis.improvement')
  const labelDeterioration = UseI18n('riskComponentAnalysis.deterioration')
  return (
    <div className={s.yAxisLabel}>
      <div className={s.up}>
        <span className={s.improvement}>
          <svg width="20" height="85" viewBox="0 0 20 100">
            <text
              x="10"
              y="55"
              textAnchor="middle"
              transform="rotate(360,10,90)"
              fill="#4caf50"
              style={{ fontSize: 15, fontWeight: 400 }}
            >
              {labelImprovement}
            </text>
          </svg>
        </span>
        <Image src={arrowUp} alt="arrow" className={s.arrowUp} />
      </div>
      <div className={s.median}>
        <span className={s.clamp4}>
          <Translate value="riskComponentAnalysis.segmentMedianScore" />
        </span>
        <span className={s.score}>({medianScore})</span>
      </div>
      <div className={s.down}>
        <span className={s.deterioration}>
          <svg width="20" height="85" viewBox="0 0 20 100">
            <text
              x="10"
              y="45"
              textAnchor="middle"
              transform="rotate(360,10,90)"
              fill="#e53935"
              style={{ fontSize: 15, fontWeight: 400 }}
            >
              {labelDeterioration}
            </text>
          </svg>
        </span>
        <Image src={arrowDown} alt="arrow" className={s.arrowDown} />
      </div>
    </div>
  )
}
